import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Switch} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Pill} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ApolloQueryResult} from 'apollo-client';
import copyToClipboard from 'copy-to-clipboard';
import React, {FC, useContext, useMemo, useState} from 'react';
import {CellInfo, Column} from 'react-table';
import {toast} from 'react-toastify';

import config from '../../config';
import {
  useOauthClientsQuery,
  useTeamOrganizationQuery,
} from '../../generated/graphql';
import {MemberData, TeamBaseEntity} from '../../graphql/teamQuery';
import {TeamPageQueryData} from '../../pages/Team/types';
import {useViewer} from '../../state/viewer/hooks';
import {useIsServiceAccountsEnabled} from '../../util/accounts/pricing';
import {useAdminModeActive} from '../../util/admin';
import DOC_URLS from '../../util/doc_urls';
import {
  checkIsOrgServiceAccount,
  checkIsServiceAccount,
} from '../../util/serviceAccounts';
import * as urls from '../../util/urls';
import {WANDB_ENTITY_NAME} from '../Launch/publicQueueUtils';
import {LightBulbInfo} from '../LightBulbInfo';
import {PublicQueueLaunchAgentSwitch} from '../MembersTable/TeamMembersTable/LaunchAgentSwitch';
import {TeamMemberRole} from '../MembersTable/TeamMembersTable/utils';
import {OrganizationMemberRole} from '../MembersTable/util';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import WBReactTable from '../WBReactTable';
import {DeleteServiceAccountModal} from './DeleteServiceAccountModal';
import {
  ServiceAccountAuthMethod,
  ServiceAccountAuthPill,
} from './ServiceAccountAuthPill';
import {ServiceAccountDrawer} from './ServiceAccountDrawer';

type ServiceAccountsTableProps = {
  entity: TeamBaseEntity;
  refetchTeamData?: () => Promise<ApolloQueryResult<TeamPageQueryData>>;
};

type ServiceAccount = {
  serviceAccountId: string;
  clientId?: string;
  subject?: string;
  apiKey?: string;
  authMethod: ServiceAccountAuthMethod;
  name: string;
  isPublicQueueLaunchAgent: boolean;
  isOrgServiceAccount: boolean;
};

export const ServiceAccountsTable: FC<ServiceAccountsTableProps> = ({
  entity,
  refetchTeamData,
}) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  // technically you should have a valid license to use service accounts in onprem environments,
  // but since you can't create teams without a license anyway, checking the config is fine for now until we
  // get account selector in server
  const hasServiceAccountAccess =
    useIsServiceAccountsEnabled(selectedAccount) ||
    config.ENVIRONMENT_IS_PRIVATE;

  const viewer = useViewer();
  const adminModeEnabled =
    useAdminModeActive() && !viewer?.instanceAdminTeamAccessDisabled;

  const viewerIsTeamAdmin =
    entity?.members.find(m => m.id === viewer?.id)?.memberRole.name ===
    TeamMemberRole.ADMIN;
  const viewerCanCreateSA = adminModeEnabled || viewerIsTeamAdmin;

  const {data: organizationData} = useTeamOrganizationQuery({
    variables: {entityName: entity.name},
  });

  const organization = organizationData?.entity?.organization;

  const viewerIsOrgAdmin =
    organization?.members.find(m => m.username === viewer?.username)?.role ===
    OrganizationMemberRole.ADMIN;

  // The only entity that can have a public queue launch agent is the wandb entity
  // and we only allow wandb admins to configure service accounts as public queue
  // launch agents.
  const isWandbEntity = entity.name === WANDB_ENTITY_NAME;
  const canManagePublicQueueLaunchAgents = adminModeEnabled && isWandbEntity;

  const {data, refetch: refetchClients} = useOauthClientsQuery({
    variables: {
      entityName: entity.name,
    },
  });

  const refetchAllData = () => {
    if (refetchTeamData != null) {
      refetchTeamData();
    }
    refetchClients();
  };

  const serviceAccounts = entity.members.filter(m =>
    checkIsServiceAccount(m.accountType)
  );

  const oauthClients = useMemo(() => {
    return (
      data?.entity?.members.filter(
        m => m.OAuthClients && m.OAuthClients.length > 0
      ) ?? []
    );
  }, [data?.entity?.members]);

  const copyApiKey = async (apiKey: string) => {
    try {
      // try to use the modern clipboard API
      await navigator.clipboard.writeText(apiKey);
    } catch {
      copyToClipboard(apiKey);
    }
    toast('Copied api key to clipboard');
  };

  const [isSADrawerOpen, setIsSADrawerOpen] = useState<boolean>(false);

  const [isDeleteSAModalOpen, setIsDeleteSAModalOpen] =
    useState<boolean>(false);
  const [saToDelete, setSAToDelete] = useState<ServiceAccount>();

  const columns = useMemo(() => {
    const baseColumns: Column[] = [
      {
        Header: 'Name',
        accessor: 'name',
        minWidth: 100,
        Cell: (cellInfo: CellInfo) => {
          return <div className="break-words">{cellInfo.value}</div>;
        },
      },
      {
        Header: 'Account Type',
        accessor: 'accountType',
        minWidth: 100,
        Cell: (cellInfo: CellInfo) => {
          return cellInfo.value.isOrgServiceAccount ? (
            <Pill className="ml-6" label="Org" color="sienna" />
          ) : (
            <Pill className="ml-6" label="Team" color="teal" />
          );
        },
      },
      {
        Header: 'Auth Method',
        accessor: 'authMethod',
        minWidth: 100,
        Cell: (cellInfo: CellInfo) => {
          return (
            <div className="flex flex-row">
              {cellInfo.value}
              <ServiceAccountAuthPill authMethod={cellInfo.value} />
            </div>
          );
        },
      },
    ];

    const subjectCol = {
      Header: 'Subject',
      accessor: 'subject',
      headerStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      style: {
        display: 'flex',
        justifyContent: 'center',
      },
      minWidth: 70,
      Cell: (cellInfo: CellInfo) => {
        return <div>{cellInfo.value}</div>;
      },
    };

    const publicQueueLaunchAgentCol = {
      Header: 'Public Queue Launch Agent',
      headerStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      accessor: 'publicQueueLaunchAgent',
      minWidth: 70,
      style: {
        display: 'flex',
        justifyContent: 'center',
      },
      sortMethod: (
        a: {isPublicQueueLaunchAgent: boolean},
        b: {isPublicQueueLaunchAgent: boolean}
      ) => {
        if (a.isPublicQueueLaunchAgent === b.isPublicQueueLaunchAgent) {
          return 0;
        }
        return a.isPublicQueueLaunchAgent ? 1 : -1;
      },
      Cell: (cellInfo: CellInfo) => {
        const {serviceAccountId, authMethod} = cellInfo.value;
        return cellInfo.value.isOrgServiceAccount ? (
          <Switch.Root disabled style={{opacity: 0.5}}>
            <Switch.Thumb checked={false} />
          </Switch.Root>
        ) : authMethod === ServiceAccountAuthMethod.FEDERATED ? (
          <PublicQueueLaunchAgentSwitch
            entity={entity}
            isViewerAdmin={canManagePublicQueueLaunchAgents}
            userId={serviceAccountId}
            refetch={refetchTeamData}
          />
        ) : (
          <div className="text-sm text-moon-500">Not available</div>
        );
      },
    };

    const apiKeyCol = {
      accessor: 'apiKey',
      minWidth: 70,
      style: {
        display: 'flex',
        alignItems: 'center',
      },
      Cell: (cellInfo: CellInfo) => {
        if (cellInfo.value.isOrgServiceAccount && !viewerIsOrgAdmin) {
          return <Button variant="ghost" disabled={true} />;
        }

        return (
          cellInfo.value.apiKey && (
            <TargetBlank
              data-test="copy-api-key-button"
              className={`flex flex-row hover:text-teal-600 ${
                cellInfo.value.isOrgServiceAccount ? 'pointer-events-none' : ''
              }`}
              onClick={() => copyApiKey(cellInfo.value.apiKey)}>
              <Icon name="copy" className="mr-4" />
              Copy API key
            </TargetBlank>
          )
        );
      },
    };

    const deleteCol = {
      Header: '',
      accessor: 'delete',
      maxWidth: 70,
      Cell: (cellInfo: CellInfo) => {
        return (
          <>
            <Button
              variant="ghost"
              icon="delete"
              onClick={() => {
                setSAToDelete(cellInfo.value);
                setIsDeleteSAModalOpen(true);
              }}
              disabled={cellInfo.value.isOrgServiceAccount}
            />
          </>
        );
      },
    };

    const cols = [...baseColumns];
    if (oauthClients.length > 0) {
      cols.push(subjectCol);
    }
    if (viewerCanCreateSA) {
      if (oauthClients.length !== serviceAccounts.length) {
        cols.push(apiKeyCol);
      }

      if (canManagePublicQueueLaunchAgents) {
        cols.push(publicQueueLaunchAgentCol);
      }

      cols.push(deleteCol);
    }
    return cols;
  }, [
    viewerCanCreateSA,
    entity,
    refetchTeamData,
    setIsDeleteSAModalOpen,
    setSAToDelete,
    oauthClients,
    serviceAccounts,
    viewerIsOrgAdmin,
    canManagePublicQueueLaunchAgents,
  ]);

  const rows = useMemo(
    () =>
      entity?.members
        .filter(m => checkIsServiceAccount(m.accountType))
        .map((member: MemberData) => {
          const {apiKey, name, id} = member;
          const authMethod = apiKey
            ? ServiceAccountAuthMethod.API_KEY
            : ServiceAccountAuthMethod.FEDERATED;
          const memberClients =
            oauthClients.find(m => m.id === id)?.OAuthClients ?? [];
          const isOrgServiceAccount = checkIsOrgServiceAccount(
            member.accountType
          );
          const serviceAccount: ServiceAccount = {
            serviceAccountId: id,
            name,
            apiKey,
            authMethod,
            // service accounts can only have 1 client for now
            clientId:
              memberClients.length > 0 ? memberClients[0].id : undefined,
            subject:
              memberClients.length > 0 ? memberClients[0].subject : undefined,
            isOrgServiceAccount,
            isPublicQueueLaunchAgent:
              entity.publicQueueLaunchAgents?.some(agent => agent.id === id) ??
              false,
          };
          const row = {
            ...serviceAccount,
            apiKey: serviceAccount,
            accountType: serviceAccount,
            delete: serviceAccount,
            publicQueueLaunchAgent: serviceAccount,
          };
          return {
            searchString: name,
            row,
          };
        }),
    [entity.members, entity.publicQueueLaunchAgents, oauthClients]
  );

  const docsButton = (
    <TargetBlank href={DOC_URLS.serviceAccounts}>
      <Button className="mr-10" endIcon="open-new-tab" variant="secondary">
        Documentation
      </Button>
    </TargetBlank>
  );

  const newSAButton = (
    <Button
      data-test="new-service-account-button"
      className="mr-10"
      icon="add-new"
      onClick={() => setIsSADrawerOpen(true)}>
      New Team Service Account
    </Button>
  );

  if (!hasServiceAccountAccess || serviceAccounts.length === 0) {
    return (
      <Tailwind>
        <div className="mb-32 rounded-lg border border-moon-250 bg-white px-32 py-24">
          <LightBulbInfo />
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold">Service Accounts</h2>
            <p className="mb-30 text-lg">
              Service Accounts allow non-human users to access Weights & Biases.{' '}
              {!hasServiceAccountAccess &&
                'You need to upgrade to access this feature.'}
            </p>
            <div className="flex flex-row items-center">
              {docsButton}
              {!hasServiceAccountAccess ? (
                <TargetBlank href={urls.contactSalesPricing()}>
                  <Button>Contact Sales</Button>
                </TargetBlank>
              ) : (
                viewerCanCreateSA && newSAButton
              )}
            </div>
          </div>
        </div>
        <ServiceAccountDrawer
          isOpen={isSADrawerOpen}
          close={() => setIsSADrawerOpen(false)}
          refetch={refetchAllData}
          entityName={entity.name}
        />
      </Tailwind>
    );
  }

  return (
    <Tailwind>
      <WBReactTable
        dataTest="service-accounts-table"
        extraActions={
          <>
            {docsButton}
            {viewerCanCreateSA && newSAButton}
          </>
        }
        data={rows}
        columns={columns}
        defaultSorted={[{id: 'name', desc: true}]}
        pageSize={20}
        getTrProps={(state: any, rowInfo: any) => ({
          className: 'flex items-center min-h-64',
        })}
      />
      {saToDelete && (
        <DeleteServiceAccountModal
          isOpen={isDeleteSAModalOpen}
          close={() => setIsDeleteSAModalOpen(false)}
          id={saToDelete.serviceAccountId}
          name={saToDelete.name}
          clientId={saToDelete.clientId}
          entityName={entity.name}
          refetch={refetchTeamData}
        />
      )}
      <ServiceAccountDrawer
        isOpen={isSADrawerOpen}
        close={() => setIsSADrawerOpen(false)}
        refetch={refetchAllData}
        entityName={entity.name}
      />
    </Tailwind>
  );
};
